/**
 * Progress of a deletion driven from an uploaded list of ids. Present only on jobs started that way.
 */
export interface BulkDeletionState {
  reason?: string | null;
  /** SharePoint was left untouched and only NFS's records were deleted. */
  recordsOnly?: boolean;
  totalRows: number;
  deletedCount: number;
  notFoundCount: number;
  malformedCount: number;
  failedCount: number;
  failuresCsvUrl?: string | null;
}

/** Whether the uploaded ids name files or the entities that own them. */
export type BulkDeletionMode = "entities" | "files";

/**
 * Largest list the portal will send, at roughly 1.6 million ids.
 *
 * The real ceiling is the ingress's request body limit, which also bounds how much of a list a deletion run
 * holds in memory. The margin under it covers the multipart framing and the reason that travel with the file,
 * and leaves an over-limit list refused here rather than by a proxy error page the API never sees.
 */
export const BULK_DELETION_MAX_CSV_BYTES = 60 * 1024 * 1024;

export const formatBytesAsMb = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export interface BulkDeletionFamily {
  /** Route segment for an upload of entity ids. */
  entitiesRoute: string;
  /** Route segment for an upload of file ids. */
  filesRoute: string;
  entitiesLabel: string;
  filesLabel: string;
}

export const BULK_DELETION_FAMILIES = {
  cases: {
    entitiesRoute: "cases/delete/bulk",
    filesRoute: "case/files/delete/bulk",
    entitiesLabel: "Cases",
    filesLabel: "Case files",
  },
  intakes: {
    entitiesRoute: "intakes/delete/bulk",
    filesRoute: "intake/files/delete/bulk",
    entitiesLabel: "Intakes",
    filesLabel: "Intake files",
  },
  providers: {
    entitiesRoute: "providers/delete/bulk",
    filesRoute: "provider/files/delete/bulk",
    entitiesLabel: "Providers",
    filesLabel: "Provider files",
  },
  templates: {
    entitiesRoute: "templates/delete/bulk",
    filesRoute: "template/files/delete/bulk",
    entitiesLabel: "Template libraries",
    filesLabel: "Template files",
  },
  firmDocuments: {
    entitiesRoute: "firmDocumentsFolders/delete/bulk",
    filesRoute: "firmDocumentsFolder/files/delete/bulk",
    entitiesLabel: "Firm documents folders",
    filesLabel: "Firm documents",
  },
} as const satisfies Record<string, BulkDeletionFamily>;

export type BulkDeletionFamilyKey = keyof typeof BULK_DELETION_FAMILIES;

export const bulkDeletionRoute = (
  family: BulkDeletionFamilyKey,
  mode: BulkDeletionMode
): string =>
  mode === "entities"
    ? BULK_DELETION_FAMILIES[family].entitiesRoute
    : BULK_DELETION_FAMILIES[family].filesRoute;

export const bulkDeletionModeLabel = (
  family: BulkDeletionFamilyKey,
  mode: BulkDeletionMode
): string =>
  mode === "entities"
    ? BULK_DELETION_FAMILIES[family].entitiesLabel
    : BULK_DELETION_FAMILIES[family].filesLabel;
